import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getVendorClients, getVendorRequests } from './api';
import './VendorDashboard.css';

interface Client {
  clientId: string;
  clientName: string;
  email: string;
  timestamp: string;
}

interface SongRequest {
  name: string;
  artist: string;
  youtubeId: string;
}

interface SectionRequest {
  title: string;
  songs: SongRequest[];
}

interface ClientRequest {
  requestId: string;
  clientName: string;
  clientEmail: string;
  requestData: string;
  status: string;
  timestamp: string;
}

function VendorDashboard() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [requests, setRequests] = useState<ClientRequest[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<ClientRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAuthRequired, setShowAuthRequired] = useState(false);
  const [vendorName] = useState(() => {
    return localStorage.getItem('prelude_vendor_name') || 'Your Business';
  });
  const [vendorId] = useState(() => {
    return localStorage.getItem('prelude_vendor_id') || 'default';
  });

  // Check if user is authenticated as vendor
  useEffect(() => {
    const userEmail = localStorage.getItem('prelude_user_email');
    const userType = localStorage.getItem('prelude_user_type');

    if (!userEmail || userType !== 'vendor') {
      setShowAuthRequired(true);
    }
  }, []);

  // Fetch clients and requests from backend
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [clientsData, requestsData] = await Promise.all([
          getVendorClients(vendorId),
          getVendorRequests(vendorId)
        ]);
        setClients(clientsData);
        setRequests(requestsData);
      } catch (err) {
        setError('Failed to load data from server');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [vendorId]);

  const getClientRequests = (clientEmail: string): ClientRequest[] => {
    return requests.filter(req => req.clientEmail === clientEmail);
  };

  const parseRequestData = (requestData: string): SectionRequest[] => {
    try {
      return JSON.parse(requestData);
    } catch {
      return [];
    }
  };

  const formatDate = (timestamp: string): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateForFilename = (timestamp: string): string => {
    const date = new Date(timestamp);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const handleSaveAsPDF = (request: ClientRequest) => {
    const sections = parseRequestData(request.requestData);
    const dateStr = formatDateForFilename(request.timestamp);
    const clientName = request.clientName.replace(/[^a-zA-Z0-9]/g, '_');
    const filename = `${dateStr}_${clientName}_SongRequests.html`;

    // Create HTML content for PDF
    let htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Song Requests - ${request.clientName}</title>
  <style>
    body { font-family: Georgia, serif; margin: 40px; color: #333; }
    h1 { color: #5a4a42; border-bottom: 2px solid #c9a86c; padding-bottom: 10px; }
    h2 { color: #8b7355; margin-top: 30px; }
    .info { background: #f5f0eb; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
    .section { margin-bottom: 25px; }
    .section h3 { color: #5a4a42; margin-bottom: 10px; }
    ul { list-style: none; padding: 0; }
    li { padding: 8px 0; border-bottom: 1px solid #d4c4b7; }
    .song-name { font-weight: bold; }
    .song-artist { color: #666; font-style: italic; }
    .footer { margin-top: 40px; text-align: center; color: #888; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Song Requests</h1>
  <div class="info">
    <p><strong>Client:</strong> ${request.clientName}</p>
    <p><strong>Email:</strong> ${request.clientEmail}</p>
    <p><strong>Submitted:</strong> ${formatDate(request.timestamp)}</p>
    <p><strong>Status:</strong> ${request.status}</p>
  </div>
`;

    sections.forEach(section => {
      htmlContent += `
  <div class="section">
    <h3>${section.title}</h3>
    ${section.songs.length === 0 ? '<p><em>No songs selected</em></p>' : `
    <ul>
      ${section.songs.map(song => `
      <li>
        <span class="song-name">${song.name}</span><br>
        <span class="song-artist">by ${song.artist}</span>
      </li>
      `).join('')}
    </ul>
    `}
  </div>
`;
    });

    htmlContent += `
  <div class="footer">
    <p>Generated by Prelude Music Portal</p>
  </div>
</body>
</html>
`;

    // Create and download the file
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="vendor-dashboard">
        <div className="loading-screen">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="vendor-dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <Link to="/" className="back-link">← Home</Link>
          <h1>Prelude - {vendorName} Dashboard</h1>
        </div>
        <div className="header-right">
          <Link to="/test" className="nav-link">Client View</Link>
          <Link to="/portal" className="nav-link">Portal</Link>
        </div>
      </header>

      {error && <div className="error-banner">{error}</div>}

      <div className="dashboard-content">
        <div className="clients-panel">
          <h2>Your Clients</h2>
          <div className="clients-list">
            {clients.length === 0 ? (
              <div className="no-clients">No clients registered yet</div>
            ) : (
              clients.map(client => (
                <div
                  key={client.clientId}
                  className={`client-card ${selectedClient?.clientId === client.clientId ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedClient(client);
                    setSelectedRequest(null);
                  }}
                >
                  <div className="client-name">{client.clientName}</div>
                  <div className="client-email">{client.email}</div>
                  <div className="client-date">Registered: {formatDate(client.timestamp)}</div>
                  <div className="request-count">
                    {getClientRequests(client.email).length} request(s)
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="requests-panel">
          {selectedClient ? (
            <>
              <h2>Requests from {selectedClient.clientName}</h2>
              <div className="requests-list">
                {getClientRequests(selectedClient.email).length === 0 ? (
                  <div className="no-requests">No requests yet</div>
                ) : (
                  getClientRequests(selectedClient.email).map(request => (
                    <div
                      key={request.requestId}
                      className={`request-card ${selectedRequest?.requestId === request.requestId ? 'selected' : ''}`}
                      onClick={() => setSelectedRequest(request)}
                    >
                      <div className="request-date">{formatDate(request.timestamp)}</div>
                      <div className="request-status">{request.status}</div>
                      <div className="request-preview">
                        {parseRequestData(request.requestData).reduce(
                          (total, section) => total + section.songs.length,
                          0
                        )} songs selected
                      </div>
                      <button
                        className="pdf-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveAsPDF(request);
                        }}
                      >
                        Save as PDF
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <h2>Client Requests</h2>
              <p>Select a client to view their song requests</p>
            </div>
          )}
        </div>

        <div className="request-details-panel">
          {selectedRequest ? (
            <>
              <h2>Request Details</h2>
              <div className="request-info">
                <div>Submitted: {formatDate(selectedRequest.timestamp)}</div>
                <div>Status: <span className="status-badge">{selectedRequest.status}</span></div>
              </div>
              <div className="sections-list">
                {parseRequestData(selectedRequest.requestData).map((section, idx) => (
                  <div key={idx} className="request-section">
                    <h3>{section.title}</h3>
                    {section.songs.length === 0 ? (
                      <div className="no-songs">No songs selected</div>
                    ) : (
                      <ul>
                        {section.songs.map((song, songIdx) => (
                          <li key={songIdx}>
                            <span className="song-name">{song.name}</span>
                            <span className="song-artist">by {song.artist}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <h2>Request Details</h2>
              <p>Select a request to view song selections</p>
            </div>
          )}
        </div>
      </div>

      {showAuthRequired && (
        <div className="auth-required-overlay">
          <div className="auth-required-modal">
            <h3>Sign In Required</h3>
            <p>You need to sign in as a vendor to access this dashboard.</p>
            <button
              className="go-to-portal-button"
              onClick={() => navigate('/portal')}
            >
              Go to Portal →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default VendorDashboard;
